export type ImageMask = 'none' | 'rounded' | 'circle'

export const IMAGE_MASKS: ImageMask[] = ['none', 'rounded', 'circle']

const ANIMATED_MIME_TYPES = ['image/gif', 'image/webp']
const ANIMATED_EXTENSIONS = /\.(gif|webp)(?:[?#].*)?$/i

export const isAnimatedImage = (source: File | Blob | string | null | undefined): boolean => {
  if (!source) return false
  if (typeof source === 'string') return ANIMATED_EXTENSIONS.test(source)
  return ANIMATED_MIME_TYPES.includes(source.type)
}

const loadImage = (source: File | Blob | string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source)
    const img = new Image()
    if (typeof source === 'string') img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => {
      if (typeof source !== 'string') URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

export const getImageDimensions = async (source: File | Blob): Promise<{ width: number; height: number }> => {
  const img = await loadImage(source)
  URL.revokeObjectURL(img.src)
  return { width: img.naturalWidth, height: img.naturalHeight }
}

const drawRoundedRect = (ctx: CanvasRenderingContext2D, w: number, h: number, r: number) => {
  ctx.beginPath()
  ctx.moveTo(r, 0)
  ctx.arcTo(w, 0, w, h, r)
  ctx.arcTo(w, h, 0, h, r)
  ctx.arcTo(0, h, 0, 0, r)
  ctx.arcTo(0, 0, w, 0, r)
  ctx.closePath()
}

const drawEllipse = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  ctx.beginPath()
  ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
  ctx.closePath()
}

const drawMaskPath = (ctx: CanvasRenderingContext2D, mask: ImageMask, w: number, h: number) => {
  switch (mask) {
    case 'rounded':
      drawRoundedRect(ctx, w, h, Math.min(w, h) * 0.08)
      return
    case 'circle':
      drawEllipse(ctx, w, h)
      return
  }
}

/**
 * Apply a shape mask to an image. Returns a new PNG File with transparent
 * areas where the shape is cut out. For mask='none', returns the source as-is
 * (or as a File if input was a Blob/URL).
 */
export const applyImageMask = async (source: File | Blob | string, mask: ImageMask): Promise<File> => {
  if (mask === 'none') {
    if (source instanceof File) return source
    if (source instanceof Blob) return new File([source], 'image.png', { type: source.type || 'image/png' })
    const response = await fetch(source)
    const blob = await response.blob()
    return new File([blob], 'image.png', { type: blob.type || 'image/png' })
  }

  const img = await loadImage(source)

  // For circle, crop source to a centered square first → produces a true circle
  // regardless of source aspect ratio (avatar-style). Other masks preserve aspect.
  const cropToSquare = mask === 'circle'
  const size = cropToSquare ? Math.min(img.naturalWidth, img.naturalHeight) : 0
  const canvasW = cropToSquare ? size : img.naturalWidth
  const canvasH = cropToSquare ? size : img.naturalHeight
  const srcX = cropToSquare ? (img.naturalWidth - size) / 2 : 0
  const srcY = cropToSquare ? (img.naturalHeight - size) / 2 : 0
  const srcW = cropToSquare ? size : img.naturalWidth
  const srcH = cropToSquare ? size : img.naturalHeight

  const canvas = document.createElement('canvas')
  canvas.width = canvasW
  canvas.height = canvasH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  ctx.save()
  drawMaskPath(ctx, mask, canvasW, canvasH)
  ctx.clip()
  ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, canvasW, canvasH)
  ctx.restore()

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas toBlob returned null'))
        return
      }
      resolve(new File([blob], `masked-${mask}.png`, { type: 'image/png' }))
    }, 'image/png')
  })
}

/**
 * CSS clip-path equivalent (for editor preview only — NOT email-safe).
 * Returns a string suitable for the CSS `clip-path` property.
 */
export const maskToClipPath = (mask: ImageMask): string => {
  switch (mask) {
    case 'circle':
      return 'ellipse(50% 50% at 50% 50%)'
    default:
      return 'none'
  }
}
